import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { downloadFileFromResponse } from '@shared/utils/downloadFileFromResponse';
import { QUOTATION_INSURANCE_CARRIER_NON_TERMINAL_STATES } from '@shared/constants/quotationInsuranceCarrierStates';
import { createQuotation, downloadQuotationLog, downloadRpaTrace, getQuotation, getQuotationExecutionDetails, getQuotations, updateQuotation } from '../services/quotationsService';

const BASE = 'quotations';

export const quotationsQueryKeys = {
  base: () => [BASE],
  detail: (id) => [BASE, 'detail', id],
  list: (data) => (data ? [BASE, 'list', data] : [BASE, 'list']),
  executionDetails: ({ quotationId, quotationInsuranceCarrierId }) => [BASE, 'detail', quotationId, 'execution-details', quotationInsuranceCarrierId],
};

export const useGetQuotation = (id) => {
  return useQuery({
    queryKey: quotationsQueryKeys.detail(id),
    queryFn: () => getQuotation(id),
    enabled: !!id,
  });
};

export const useGetQuotations = (data) => {
  return useQuery({
    queryKey: quotationsQueryKeys.list(data),
    placeholderData: keepPreviousData,
    queryFn: () => getQuotations(data),
  });
};

export const useCreateQuotation = () => {
  return useMutation({
    mutationFn: (data) => createQuotation(data),
  });
};

export const useUpdateQuotation = () => {
  return useMutation({
    mutationFn: ({ id, data }) => updateQuotation(id, data),
  });
};

export const useDownloadQuotationLog = () => {
  return useMutation({
    mutationFn: (data) => downloadQuotationLog(data.id, {
      quotationInsuranceCarrierId: data.quotationInsuranceCarrierId,
      fileFormat: 'txt',
    }),
    onSuccess: (response) => {
      downloadFileFromResponse(response, { fallbackFileName: 'requests-responses.txt' });
    },
  });
};

export const useDownloadRpaTrace = () => {
  return useMutation({
    mutationFn: (data) => downloadRpaTrace(data.id, { quotationInsuranceCarrierId: data.quotationInsuranceCarrierId }),
    onSuccess: (response) => {
      downloadFileFromResponse(response, { fallbackFileName: 'rpa-trace.zip' });
    },
  });
};

export const EXECUTION_DETAILS_POLL_INTERVAL_MS = 10000;

export const useGetQuotationExecutionDetails = ({ quotationId, quotationInsuranceCarrierId }, { enabled = true, pollWhileNonTerminal = false } = {}) => {
  return useQuery({
    queryKey: quotationsQueryKeys.executionDetails({ quotationId, quotationInsuranceCarrierId }),
    queryFn: () => getQuotationExecutionDetails(quotationId, quotationInsuranceCarrierId),
    enabled: enabled && !!quotationId && !!quotationInsuranceCarrierId,
    refetchInterval: pollWhileNonTerminal
      ? (query) => {
        return QUOTATION_INSURANCE_CARRIER_NON_TERMINAL_STATES.includes(query.state.data?.state)
          ? EXECUTION_DETAILS_POLL_INTERVAL_MS
          : false;
      }
      : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: pollWhileNonTerminal
      ? (query) => QUOTATION_INSURANCE_CARRIER_NON_TERMINAL_STATES.includes(query.state.data?.state)
      : false,
  });
};
